import { type AdInputLookup, type EnrichedAdInputField, getAt } from '@finn-no/mobility-item-insertion-shared';
import { i18n } from '@lingui/core';
import { Plural, Trans } from '@lingui/react/macro';
import { Button } from '@warp-ds/react';
import { useFormContext } from 'react-hook-form';
import type { WidgetModelArray } from '../../../types/widget.js';
import { focusElement } from '../../utils/focus.js';
import { getSortedErrorFields } from './utils/formErrorUtils.js';

type Props = {
  adInputLookup: AdInputLookup;
  widgetModel: WidgetModelArray;
};

export const WizardErrorSummary = ({ adInputLookup, widgetModel }: Props) => {
  const {
    formState: { errors },
  } = useFormContext();

  const sortedErrorFields = getSortedErrorFields(errors, widgetModel);

  if (errors.root?.serverError) {
    return (
      <p data-testid="summary-error" className="s-text-negative mb-16">
        {errors.root.serverError.message}
      </p>
    );
  }

  const unknownLabel = i18n.t({
    id: 'mobility-item-insertion.form.error-summary.unknown-field',
    comment:
      "In a few rare cases we can't resolve which field is erroring, in this case the string will say 'You are missing or need to correct Unknown Field'. To further let the user know that somethings not right it will also be followed by a message letting them know that they should reach out to customer support",
    message: 'Unknown field',
  });
  const uhOhMessage = i18n.t({
    id: 'mobility-item-insertion.form.error-summary.unknown-field.context',
    comment:
      'This is the follow up message letting users that they should try again, and reach out to customer support if it still fails',
    message:
      'Please refresh the page and try again. All changes will be lost. If the problem persists please reach out to customer support.',
  });

  if (sortedErrorFields.length > 0) {
    let firstLabel = getAt<EnrichedAdInputField>(
      adInputLookup,
      sortedErrorFields[0].replaceAll('.', '.children.'),
    )?.label;
    const hasLabel = !!firstLabel;

    if (!firstLabel) {
      firstLabel = unknownLabel;
    }

    return (
      <p className="px-16 pt-12 m-0" data-testid="summary-error" role="alert">
        <Plural
          id="mobility-item-insertion.form.missing-field"
          comment="Message shown when there are missing fields"
          value={sortedErrorFields.length}
          one={
            <Trans>
              You are missing or need to correct{' '}
              <Button link onClick={() => focusElement(sortedErrorFields[0])}>
                {firstLabel}
              </Button>
            </Trans>
          }
          _2={
            <Trans>
              You are missing or need to correct{' '}
              <Button link onClick={() => focusElement(sortedErrorFields[0])}>
                {firstLabel}
              </Button>{' '}
              and one other field
            </Trans>
          }
          other={
            <Trans>
              You are missing or need to correct{' '}
              <Button link onClick={() => focusElement(sortedErrorFields[0])}>
                {firstLabel}
              </Button>{' '}
              and {sortedErrorFields.length - 1} other fields
            </Trans>
          }
        />
        {!hasLabel && <p>{uhOhMessage}</p>}
      </p>
    );
  }

  return null;
};
